import { DataTable } from '../components/DataTable.tsx';
import { Badge, Card, Empty, Notice, Spinner } from '../components/Ui.tsx';
import { api } from '../lib/api.ts';
import { useAsync, useAuth } from '../lib/auth.tsx';
import { relativeTime } from '../lib/format.ts';

const TONE: Record<string, 'critical' | 'warning' | 'muted'> = {
  critical: 'critical',
  warning: 'warning',
  info: 'muted',
};

export function AlertsPage() {
  const { can } = useAuth();
  const alerts = useAsync(() => api.alerts.list(false), []);
  const undercuts = useAsync(() => api.insights.undercuts().catch(() => null), []);

  if (alerts.loading) return <Spinner />;

  const list = alerts.data?.alerts ?? [];

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Alerts</h1>
          <p>
            Threshold findings, de-duplicated so a gap you already know about does not re-send every
            run. An alert repeats only when it moves into a different severity band.
          </p>
        </div>
        <div className="toolbar">
          <button
            onClick={async () => {
              await api.alerts.evaluate();
              alerts.reload();
            }}
          >
            Re-evaluate now
          </button>
          <button
            className="ghost"
            onClick={async () => {
              await api.alerts.markAllRead();
              alerts.reload();
            }}
            disabled={(alerts.data?.unread ?? 0) === 0}
          >
            Mark all read
          </button>
        </div>
      </header>

      {undercuts.data && undercuts.data.findings.length > 0 && (
        <Card
          title="Live undercuts"
          subtitle="Current state, recomputed on load - not a notification history"
        >
          <DataTable
            rows={undercuts.data.findings}
            rowKey={(f) => f.itemCode}
            initialSort={{ key: 'gap', dir: 'desc' }}
            searchPlaceholder="Search items"
            pageSize={25}
            columns={[
              {
                key: 'item',
                header: 'Item',
                searchText: (f) => `${f.displayName} ${f.itemCode}`,
                sortValue: (f) => f.displayName,
                render: (f) => <strong>{f.displayName}</strong>,
              },
              {
                key: 'yours',
                header: 'Your price',
                align: 'num',
                sortValue: (f) => f.yourPrice,
                render: (f) => `$${f.yourPrice.toFixed(2)}`,
              },
              {
                key: 'market',
                header: 'Market',
                align: 'num',
                sortValue: (f) => f.marketPrice,
                render: (f) => `$${f.marketPrice.toFixed(2)}`,
              },
              {
                key: 'gap',
                header: 'Gap',
                align: 'num',
                sortValue: (f) => f.gapPct,
                render: (f) => `+${f.gapPct.toFixed(1)}%`,
              },
              {
                key: 'severity',
                header: 'Severity',
                sortValue: (f) => ({ critical: 3, warning: 2, info: 1 })[f.severity] ?? 0,
                render: (f) => <Badge tone={TONE[f.severity] ?? 'muted'}>{f.severity}</Badge>,
              },
            ]}
          />
        </Card>
      )}

      <Card title="Alert history">
        {list.length === 0 ? (
          <Empty title="No alerts yet">
            <span className="muted">
              Alerts appear once benchmarks are confident enough to be worth acting on.
            </span>
          </Empty>
        ) : (
          <div className="stack">
            {list.map((alert) => (
              <div
                key={alert.id}
                className="spread"
                style={{
                  padding: '11px 13px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  background: alert.readAt ? 'transparent' : 'var(--surface-2)',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div className="row" style={{ gap: 8, marginBottom: 3 }}>
                    <Badge tone={TONE[alert.severity] ?? 'muted'}>{alert.type}</Badge>
                    <strong>{alert.title}</strong>
                  </div>
                  <div className="small muted">{alert.body}</div>
                </div>
                <div className="row" style={{ flexShrink: 0 }}>
                  <span className="small muted">{relativeTime(alert.createdAt)}</span>
                  {!alert.readAt && (
                    <button
                      className="ghost"
                      onClick={async () => {
                        await api.alerts.markRead(alert.id);
                        alerts.reload();
                      }}
                    >
                      Mark read
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {!can('alerts.discord') && (
        <div style={{ marginTop: 16 }}>
          <Notice>
            <strong>Discord delivery is a Pro feature.</strong> On lower tiers alerts live in the
            dashboard only. Thresholds are configurable either way under Settings.
          </Notice>
        </div>
      )}
    </>
  );
}
